import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useCookies } from 'next-client-cookies';
import { Product } from 'shared/types/product';
import Loader from 'components/commercetools-ui/atoms/loader';
import AlertBanner from 'components/commercetools-ui/molecules/alert-banner';
import CuratedCtaCard from 'components/commercetools-ui/molecules/curated-cta-card';
import Tile, { Props as TileProps } from 'components/commercetools-ui/molecules/curated-tile';
import PersonalizedTile from 'components/commercetools-ui/organisms/personalized-banner/Tile';
import { CloseIcon } from 'components/icons';
import { AccountContext } from 'context/account';
import { fontStyleMap, positionMap, WHITE_COLOR_HEX } from 'helpers/constants';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { setupImageOptions } from 'helpers/imageData';
import { transformPhotosForMediaclipBeautyShots } from 'helpers/mediaclip/mediaclip-hub';
import { getAttributeValueLowerCase } from 'helpers/utils/attributes';
import { getCustomizationData, setCustomizationData } from 'helpers/utils/customizationStorage';
import { desktop, mediumDesktop, mobile, tablet } from 'helpers/utils/screensizes';
import { cleanString, isNonNullish } from 'helpers/utils/util';
import { sdk } from 'sdk';
import { useProduct, useProjectSettings, useWishlist } from 'frontastic';
import Slider from '../../atoms/slider';
import { useUserName } from '../utils';
import { getCachedToken, orderedProjetsAsPerExpiry, setFFIDsForPagination } from './helpers';
import { ActivityDateText, CtaCardLabel, CtaCardText, TileBackImage, TileType } from './types';

export interface Props extends ActivityDateText {
  mainHeading?: string;
  subHeading?: string;
  subHeadingWithoutName?: string;
  expiresInOrStatus?: string;
  tiles: Array<TileProps['tile']> | undefined;
  backImage?: boolean;
  tileCtaText?: string;
  ctaCardLabel?: CtaCardLabel[] | [];
  ctaText?: CtaCardText[];
  mainHeadingTextColor?: string;
  mainHeadingTextColorDark?: string;
  ariaLabelOfPreviousButton?: string;
  ariaLabelOfNextButton?: string;
  mainHeadingFontweight?: string;
  mainHeadingFontWeightMobile?: string;
  mainHeadingPosition?: string;
  mainHeadingFontsizeMobile?: number;
  subtextFontsize?: number;
  mainHeadingFontsize?: number;
  subtextHeadingFontweight?: string;
  subtextHeadingTextColor?: string;
  subtextHeadingTextColorDark?: string;
  subtextHeadingFontsizeMobile?: number;
  tileBackImage?: TileBackImage;
  tileBackImageDark?: TileBackImage;
  tileBackColor?: string;
  tileBackColorDark?: string;
  tileTitleFontWeight?: string;
  tileTextFontSize?: number;
  autoRotation?: boolean;
  autoRotationSpeed?: number;
  showStatus?: boolean;
  showExpiresIn?: boolean;
  showSaveToProjects?: boolean;
  expiresInOrStatusFontweight?: string;
  expiresInOrStatusBgColor?: string;
  expiresFontColor?: string;
  backImageSrc?: string;
  ffidCurated?: string;
  sectionBackColor?: string;
  personalizedProductsData?: any;
  productkey?: string;
  mainHeadingWithoutUserName?: string;
  tileImgContainerBgColor?: string;
  personalizedCartBannerData: any;
}
export const adjustHeight = (sectionClass: string) => {
  const slides = document.querySelectorAll(`.${sectionClass} .swiper-slide`);
  let maxHeight = 0;

  slides.forEach((slide) => {
    const height = (slide as HTMLElement).offsetHeight;
    if (height > maxHeight) maxHeight = height;
  });

  slides.forEach((slide) => {
    (slide as HTMLElement).style.height = `${maxHeight}px`;
    const childDiv = slide.querySelector('div');
    (childDiv as HTMLElement).style.height = `${maxHeight}px`;
  });
};

const loadImageAvailability = (url: string) =>
  new Promise<boolean>((resolve) => {
    if (!url) return resolve(false);
    const ImageCtor = (globalThis as any).Image;
    if (!ImageCtor) return resolve(false);

    const img = new ImageCtor();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

const CuratedSlider: React.FC<Props> = ({ personalizedCartBannerData, ...data }) => {
  const {
    mainHeading,
    mainHeadingWithoutUserName,
    subHeading,
    subHeadingWithoutName,
    tiles,
    expiresInOrStatus,
    tileCtaText,
    ctaText,
    mainHeadingTextColor,
    mainHeadingFontweight,
    mainHeadingFontWeightMobile,
    mainHeadingPosition,
    mainHeadingFontsizeMobile,
    mainHeadingFontsize,
    subtextFontsize,
    subtextHeadingFontweight,
    subtextHeadingTextColor,
    subtextHeadingFontsizeMobile,
    ariaLabelOfPreviousButton,
    ariaLabelOfNextButton,
    tileBackColor,
    tileTitleFontWeight,
    tileTextFontSize,
    autoRotation,
    autoRotationSpeed,
    showStatus,
    showExpiresIn,
    expiresInOrStatusFontweight,
    expiresInOrStatusBgColor,
    expiresFontColor,
    showSaveToProjects,
    backImageSrc,
    ffidCurated,
    sectionBackColor,
    tileImgContainerBgColor,
    expired,
    minute,
    minutes,
    hour,
    hours,
    day,
    days,
    week,
    weeks,
  } = data;

  const activityDateText: ActivityDateText = {
    expired,
    minute,
    minutes,
    hour,
    hours,
    day,
    days,
    week,
    weeks,
  };

  const cookies = useCookies();
  const { account, accountLoaded } = useContext(AccountContext);
  const { data: wishlist, removeLineItem } = useWishlist();
  const { projectSettings } = useProjectSettings();
  const imageOptions = setupImageOptions(projectSettings);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!wishlist?.lineItems?.length) return;

    const now = Date.now();

    const expiredItems = wishlist?.lineItems?.filter((item) => {
      const expiryISO = item.custom?.fields?.expirationDate;
      if (!expiryISO) return false;

      const expiryTime =
        expiryISO.length === 10 ? new Date(`${expiryISO}T23:59:59.999Z`).getTime() : new Date(expiryISO).getTime();

      return expiryTime <= now;
    });

    if (expiredItems?.length === 0) return;

    Promise.all(expiredItems?.map((item) => removeLineItem(wishlist, item))).catch(console.error);
  }, [wishlist, removeLineItem]);

  const { query } = useProduct();
  const [product, setProduct] = useState<Product[]>([]);

  const products = useMemo(() => personalizedCartBannerData?.items || [], [personalizedCartBannerData?.items]);
  const mediaClipProjectIdDataSource = personalizedCartBannerData?.mediaClipProjectId as string;

  const getAgencyId = (product: Product) => product?.variants?.[0]?.attributes?.agency_design_id_universal;

  const hasRelatedProducts = (product: Product) =>
    Array.isArray(product?.variants?.[0]?.attributes?.related_products) &&
    product.variants[0].attributes.related_products.length > 0;

  const mediaclipProducts = useMemo(() => {
    const seenAgencyIds = new Set<string>();

    return products?.filter((product: Product) => {
      const attrs = product?.variants?.[0]?.attributes;

      if (!attrs?.mediaclip_product_id_universal) return false;

      if (hasRelatedProducts(product)) return false;

      const agencyId = getAgencyId(product);
      if (agencyId) {
        if (seenAgencyIds?.has(agencyId)) return false;
        seenAgencyIds?.add(agencyId);
      }

      return true;
    });
  }, [products]);

  const mediaclipRequestKey = useMemo(() => {
    if (!mediaclipProducts.length) return '';
    return JSON.stringify({
      products: mediaclipProducts.slice(0, 4).map((p: any) => p.productId),
      count: Math.min(mediaclipProducts.length, 4),
      projectId: mediaClipProjectIdDataSource || '',
    });
  }, [mediaclipProducts, mediaClipProjectIdDataSource]);

  const [beautyshots, setBeautyshots] = useState<Record<string, string>>({});
  const hasFetchedMediaclip = useRef(false);
  const hasFetchedFiltered = useRef(false);

  useEffect(() => {
    let isCancelled = false;
    if (!mediaclipRequestKey) return;

    if (!accountLoaded) return;

    if (hasFetchedMediaclip.current) return;
    hasFetchedMediaclip.current = true;

    const fetchBeautyShots = async () => {
      let token = cookies.get('mediaclipToken');

      if (account?.accountId) {
        try {
          token = (await getCachedToken()) as string;
          if (token) {
            const existingData = getCustomizationData();
            setCustomizationData({
              ...existingData,
              token: token,
            });
          }
        } catch (error) {
          console.error('Error fetching/storing Mediaclip token:', error);
        }
      } else {
        token = '';
      }

      if (mediaclipProducts.length === 0 || !token) {
        return;
      }

      const payload = {
        beautyshots: mediaclipProducts?.slice(0, 4).map((product: any) => {
          const attrs = product?.variants?.[0]?.attributes;

          const orientation = getAttributeValueLowerCase(attrs, 'orientation');

          let orientationValue = '';

          if (orientation === 'both' || orientation === 'landscape' || orientation === 'horizontal') {
            orientationValue = `${attrs?.mediaclip_product_id_universal}-L`;
          } else if (orientation === 'portrait' || orientation === 'vertical') {
            orientationValue = `${attrs?.mediaclip_product_id_universal}-P`;
          } else {
            orientationValue = `${attrs?.mediaclip_product_id_universal}`;
          }

          return {
            productId: orientationValue || '',
            module: product.variants[0].attributes?.mediaclip_module_universal || '',
            themeUrl:
              product.attributes?.mediaclip_theme_url_universal ||
              product.variants[0].attributes?.mediaclip_theme_url_universal ||
              '$(package:mediaclip/photobooks)/themes/default',
            projectId: mediaClipProjectIdDataSource,
          };
        }),
        token: token,
      };

      try {
        const response = await sdk.composableCommerce.mediaClip.getBatchBeautyshots(payload);

        if (!response.isError && Array.isArray(response.data.beautyshotsUrls)) {
          const urls = response.data.beautyshotsUrls;

          const retryTimes = 5;
          const retryBaseMs = 200;

          for (let attempt = 0; attempt < retryTimes; attempt++) {
            if (isCancelled) return;

            const availability = await Promise.all(urls.map((url: string) => loadImageAvailability(url)));

            const newBeautyshots: Record<string, string> = {};
            let allAvailable = true;

            mediaclipProducts?.slice(0, 4)?.forEach((product: Product, index: number) => {
              const url = urls[index];
              const key = product.productKey ?? product.productId ?? '';

              if (url && availability[index] && key) {
                newBeautyshots[key] = url;
              } else if (url && url !== '') {
                allAvailable = false;
              }
            });

            setBeautyshots((prev) => ({ ...prev, ...newBeautyshots }));

            if (allAvailable || attempt === retryTimes - 1) {
              break;
            }

            const delay = retryBaseMs * 2 ** attempt;
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      } catch (error) {
        console.error('Error fetching batch beauty shots:', error);
      }
    };

    void fetchBeautyShots();

    return () => {
      isCancelled = true;
      hasFetchedMediaclip.current = false;
    };
  }, [accountLoaded, account?.accountId, mediaclipRequestKey]);

  const limitedMediaclip = useMemo(() => {
    return mediaclipProducts.slice(0, 4);
  }, [mediaclipProducts]);

  const [filteredTiles, setFilteredTiles] = useState<Array<TileProps['tile']>>([]);
  const backImage = data.backImage;
  const isPastOrder = showStatus;
  const isReviewProjects = !showStatus && showExpiresIn;
  const isProductRecommend = !showStatus && !showExpiresIn;

  const getSectionClass = () => {
    if (isPastOrder) return 'past-order';
    if (isReviewProjects) return 'review-projects';
    return 'product-recommend';
  };

  const sectionClass = getSectionClass();

  const hasTiles = filteredTiles?.length > 0;
  const containerClass = clsx(
    'curated-slider container relative rounded-2xl px-16 sm:px-0',
    sectionClass,
    `heading-${mainHeadingPosition}`,
    {
      'mb-100 pb-20': isProductRecommend,
      'pb-0': isPastOrder,
      'overflow-hidden': isReviewProjects,
      hidden: !hasTiles,
    },
  );

  const backgroundEntityPast = isPastOrder ? 'transparent' : tileBackColor;
  const backgroundEntity = data.backImage ? backImageSrc : backgroundEntityPast;

  const userName = useUserName();
  const processedSubHeading = userName ? subHeading?.replace('{{firstName}}', userName) : subHeadingWithoutName;

  const [isMobileSize, width] = useMediaQuery(mobile);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const isPureMobile = width < tablet;

  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsLoading(false);
          return 100;
        }
        return prev + 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const categoryFontweightClass = tileTitleFontWeight && clsx([fontStyleMap[tileTitleFontWeight]]);
  const mainHeadingFontweightClass =
    mainHeadingFontweight &&
    clsx([fontStyleMap[(isPureMobile ? mainHeadingFontWeightMobile : mainHeadingFontweight) ?? 'regular']]);
  const headingPosition = mainHeadingPosition && clsx(positionMap[mainHeadingPosition]);
  const ctaCardTextFontweightClass = clsx([
    fontStyleMap[
      isPureMobile ? (ctaText?.[0]?.ctaTextFontWeightMobile ?? '400') : (ctaText?.[0]?.ctaTextFontWeight ?? '400')
    ],
  ]);
  const deskSlidesPerView = `${filteredTiles && filteredTiles?.length >= 1 ? filteredTiles.length : 1}`;
  const sectionBackground = isPastOrder ? `` : `bg-[${WHITE_COLOR_HEX}]`;

  const isSingleCard = filteredTiles?.length === 1;
  const isEnabled = isDesktopSize === isProductRecommend;

  const paddingBySection: Record<string, string> = {
    'past-order': 'p-[35px]',
    'review-projects': 'p-[45px]',
  };
  const sectionClassnames = paddingBySection[sectionClass] ?? '';
  const slidesForOtherSections = isProductRecommend ? 2 : 4;

  const shouldShowArrows = (
    sectionClass: string | null | undefined,
    { isMobileSize, isTabletSize }: { isMobileSize: boolean; isTabletSize: boolean },
  ): boolean => {
    const sectionClassLower = (sectionClass ?? '').toLowerCase();
    if (sectionClassLower === 'product-recommend') return isTabletSize;
    if (['past-order', 'review-projects'].includes(sectionClassLower)) return isMobileSize && !isTabletSize;
    return false;
  };

  const templateTile: TileType | null = filteredTiles?.[0] ?? null;

  const wishlistLoading = wishlist === undefined;
  const wishlistItems = (wishlist?.lineItems ?? []).filter((item) => !!item?.custom?.fields?.mediaClipProjectId);

  const hasWishlist = wishlistItems?.length > 0;

  const showReviewProjects = isReviewProjects && !wishlistLoading && hasWishlist;

  const sortedWishlist = wishlistItems
    ?.filter((item) => !!item?.custom?.fields?.expirationDate)
    ?.sort((a, b) => {
      const expA = new Date(a.custom!.fields!.expirationDate).getTime();
      const expB = new Date(b.custom!.fields!.expirationDate).getTime();
      return expA - expB;
    });

  const wishlistTiles: TileType[] =
    sortedWishlist.length > 0
      ? sortedWishlist.map((item) => ({
          ...templateTile,

          tileTitle: item?.variant?.attributes?.design_name_universal ?? '',
          tileSubtext: item?.variant?.attributes?.short_description_universal ?? '',

          imageSrc: item?.variant?.images?.[0] ?? '',
          imageAlt: item?.name ?? '',

          activityDate: item?.custom?.fields?.expirationDate ?? '',

          price: item?.variant?.price?.centAmount != null ? (item.variant.price.centAmount / 100).toFixed(2) : '',
          item: item,
        }))
      : [];

  useEffect(() => {
    const tilesToBeConsidered = isReviewProjects ? wishlistTiles : tiles;
    const finalTiles =
      !showStatus && !showExpiresIn ? tilesToBeConsidered : orderedProjetsAsPerExpiry(tilesToBeConsidered);

    if (isPastOrder || isReviewProjects) {
      const MAX_COUNT = isPastOrder ? 3 : 4;
      const newFinalTiles = Array.isArray(finalTiles)
        ? finalTiles.slice(0, Math.min(finalTiles.length, MAX_COUNT))
        : [];
      setFilteredTiles(newFinalTiles);
    } else {
      setFilteredTiles(finalTiles || []);
    }
  }, [wishlistTiles.length, sectionClass]);

  useEffect(() => {
    if (!filteredTiles.length) return;

    const fetchProducts = async () => {
      const productKeys = filteredTiles.map(({ productKey }: any) => productKey).filter(Boolean);

      const response = await query({
        productKeys: [...new Set(productKeys)],
      });

      const items = response?.isError || !response?.data?.items ? [] : response.data.items;
      setProduct(items);
    };

    void fetchProducts();
  }, [query, filteredTiles]);

  const productMap = useMemo<Record<string, any>>(() => {
    const map: Record<string, any> = {};
    product.forEach((p: any) => {
      if (p.productKey) {
        map[p.productKey] = { ...p, variant: p.variants?.[0] };
      }

      p.variants?.forEach((variant: any) => {
        if (variant.sku) {
          map[variant.sku] = { ...p, variant };
        }
      });
    });

    return map;
  }, [product]);

  const [filteredTileBeautyshots, setFilteredTileBeautyshots] = useState<Record<string, string>>({});

  const beautyshotRequestKey = useMemo(() => {
    if (!filteredTiles?.length) return null;
    if (!Object.keys(productMap).length) return null;

    return JSON.stringify({
      tiles: filteredTiles.map((t, i) => `${t.productKey}-${i}`),
      count: filteredTiles.length,
    });
  }, [filteredTiles, productMap]);

  useEffect(() => {
    if (mediaclipProducts.length) return;
    let cancelled = false;

    // Must come before the one-shot ref below, for the same reason as the effect above.
    if (!accountLoaded) return;

    if (hasFetchedFiltered.current) return;
    hasFetchedFiltered.current = true;

    const run = async () => {
      try {
        let photoInfos = getCustomizationData()?.photoInfos ?? [];
        if (!photoInfos.length) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          photoInfos = getCustomizationData()?.photoInfos ?? [];
        }

        const payloadPhoto = transformPhotosForMediaclipBeautyShots(photoInfos.slice(0, 8), imageOptions);

        // Guests get null back from getCachedToken(), so skipping the call returns just the same.
        const token = account?.accountId ? await getCachedToken() : null;
        if (!token) return;

        const beautyshotItems = filteredTiles
          .map((tile, index) => {
            const product = productMap[tile.productKey as string];
            const attrs = product?.variant?.attributes;
            if (!attrs?.mediaclip_product_id_universal) return null;

            let orientationValue = '';
            const orientation = getAttributeValueLowerCase(product?.variant?.attributes, 'orientation');

            if (orientation === 'both' || orientation === 'landscape' || orientation === 'horizontal') {
              orientationValue = `${product?.variant?.attributes?.mediaclip_product_id_universal}-L`;
            } else if (orientation === 'portrait' || orientation === 'vertical') {
              orientationValue = `${product?.variant?.attributes?.mediaclip_product_id_universal}-P`;
            } else {
              orientationValue = `${product?.variant?.attributes?.mediaclip_product_id_universal}`;
            }
            return {
              tileIndex: index,
              productId: orientationValue,
              module: attrs.mediaclip_module_universal,
              themeUrl:
                product?.attributes?.mediaclip_theme_url_universal ||
                attrs?.mediaclip_theme_url_universal ||
                '$(package:mediaclip/photobooks)/themes/default',
            };
          })
          .filter(isNonNullish);

        if (!beautyshotItems.length) return;

        if (payloadPhoto?.length > 0) {
          const response = await sdk.composableCommerce.mediaClip.getBatchBeautyshots({
            beautyshots: beautyshotItems.map((item) => ({
              productId: item.productId,
              module: item.module,
              themeUrl: item.themeUrl,
              photos: payloadPhoto,
            })),
          });
          if (response.isError || !Array.isArray(response.data?.beautyshotsUrls)) return;

          const urls = response.data.beautyshotsUrls;
          const availability = await Promise.all(urls.map(loadImageAvailability));

          const shots: Record<string, string> = {};
          beautyshotItems.forEach((item, i) => {
            if (!cancelled && availability[i]) {
              const tile = filteredTiles[item.tileIndex];
              const key = tile?.productKey;
              if (key) {
                shots[key] = urls[i];
              }
            }
          });

          if (!cancelled) {
            setFilteredTileBeautyshots((prev) => ({ ...prev, ...shots }));
          }
        }
      } catch (e) {
        console.error('Filtered beautyshot failed:', e);
      }
    };

    void run();

    return () => {
      cancelled = true;
      hasFetchedFiltered.current = false;
    };
  }, [accountLoaded, account?.accountId, beautyshotRequestKey, filteredTiles, productMap, projectSettings]);

  const mainHeadingReviewProject = isReviewProjects && hasWishlist ? mainHeading : '';
  const mainHeadingProductRecommend = isProductRecommend
    ? userName
      ? `${userName}, ${mainHeading}`
      : mainHeadingWithoutUserName
    : mainHeadingReviewProject;

  const processedSubHeadingReviewProject = isReviewProjects && hasWishlist ? processedSubHeading : '';
  const processedSubHeadingProductRecommend = isProductRecommend
    ? processedSubHeading
    : processedSubHeadingReviewProject;

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  return (
    <>
      {(successMessage || errorMessage) && (
        <div className="z-[999] w-full bg-white">
          <AlertBanner alertType={successMessage ? 'success' : 'delete'} className="mx-auto my-12 max-w-[1200px]">
            <div
              className="flex items-center justify-between gap-16"
              role={successMessage ? 'status' : 'alert'}
              aria-atomic="true"
            >
              <p className="text-sm">{successMessage || errorMessage}</p>
              <button
                onClick={() => {
                  setSuccessMessage(null);
                  setErrorMessage(null);
                }}
                aria-label={successMessage ? 'Dismiss success message' : 'Dismiss error message'}
              >
                <CloseIcon className="size-18" />
              </button>
            </div>
          </AlertBanner>
        </div>
      )}
      <div className={`mx-0 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0`}>
        <div
          data-testid="curated-slider"
          className={containerClass}
          style={isPastOrder ? { backgroundColor: sectionBackColor ?? 'transparent' } : {}}
          data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-curated-slider`}
        >
          <div className={clsx(isPastOrder && [sectionBackground, isPureMobile ? 'pb-20' : 'px-50 pb-50', 'mb-120'])}>
            <div
              className={`section-heading ${headingPosition}`}
              data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-section-heading`}
            >
              <div className={`flex ${positionMap[mainHeadingPosition ?? 'center']}`}>
                <h2
                  className={`mx-52 ${showStatus ? 'mt-56' : 'mt-36'} ${isDesktopSize && 'leading-[48px]'} whitespace-normal break-words ${mainHeadingFontweightClass} ${data.subHeading ? '' : 'mb-36'}`}
                  style={{
                    color: mainHeadingTextColor,
                    ...(isDesktopSize
                      ? {
                          fontSize: `${mainHeadingFontsize}px`,
                        }
                      : {
                          fontSize: `${mainHeadingFontsizeMobile}px`,
                        }),
                  }}
                  data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-main-heading`}
                >
                  {isPastOrder ? mainHeading : mainHeadingProductRecommend}
                </h2>
              </div>
              <div className={`flex ${positionMap[mainHeadingPosition ?? 'center']}`}>
                <h3
                  className={clsx(
                    'mx-52',
                    {
                      'mb-36': isSingleCard,
                      'mb-20': !isSingleCard && isPureMobile,
                      'mb-16': !isSingleCard && !isPureMobile,
                    },
                    'mobile-portrait:max-w-xs mobile-landscape:max-w-xl mt-8 whitespace-normal break-words leading-[1.5] lg:max-w-[50rem]',
                  )}
                  style={{
                    fontSize: `${subtextFontsize}px`,
                    fontWeight: `${subtextHeadingFontweight}`,
                    color: subtextHeadingTextColor,
                    ...(isPureMobile && { fontSize: `${subtextHeadingFontsizeMobile}px` }),
                  }}
                  data-ffid={`lbl-${cleanString(ffidCurated ?? '')}-sub-heading`}
                >
                  {isPastOrder ? processedSubHeading : processedSubHeadingProductRecommend}
                </h3>
              </div>
            </div>
            {isLoading ? (
              <Loader progress={progress} data-ffid="ctr-loader" />
            ) : (
              <div>
                <div
                  className={`relative flex items-stretch`}
                  data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-slider-content`}
                >
                  <div
                    className={clsx(`w-full ${+deskSlidesPerView <= 2 ? 'two-slides' : 'more-slides'} bleed-right`)}
                    data-ffid={`ctr-${cleanString(ffidCurated ?? '')}-tiles-container`}
                  >
                    {isProductRecommend && !isDesktopSize ? (
                      <div>
                        {mediaclipProducts.length > 0
                          ? limitedMediaclip.map((item: Product, index: number) => (
                              <PersonalizedTile
                                key={`${item?.name}-${index}`}
                                keyprop={index}
                                product={item}
                                beautyshot={beautyshots[item.productKey ?? item.productId ?? '']}
                                previewLabel={tileCtaText}
                                saveToProjects={showSaveToProjects ? 'Save to projects +' : undefined}
                                onSuccess={(msg: string) => {
                                  setSuccessMessage(msg);
                                  setErrorMessage(null);
                                }}
                                onError={(msg: string) => {
                                  setErrorMessage(msg);
                                  setSuccessMessage(null);
                                }}
                                data={data}
                              />
                            ))
                          : filteredTiles
                            ? filteredTiles
                                ?.slice(0, 4)
                                ?.map((tile, index) => {
                                  const productKey = tile.productKey as string;
                                  const matchedProduct = productKey ? productMap[productKey] : undefined;
                                  const beautyShot = filteredTileBeautyshots[productKey];
                                  return (
                                    <Tile
                                      ffidCurated={ffidCurated}
                                      expiresFontColor={expiresFontColor}
                                      isSingleCard={isSingleCard}
                                      key={'filteredTiles' + index}
                                      keyProp={index}
                                      tile={tile}
                                      product={matchedProduct}
                                      tileCtaText={tileCtaText}
                                      backImage={backImage}
                                      backgroundEntity={backgroundEntity}
                                      expiresInOrStatus={expiresInOrStatus as string}
                                      categoryFontweightClass={categoryFontweightClass}
                                      showStatus={showStatus}
                                      showExpiresIn={showExpiresIn}
                                      expiresInOrStatusFontweight={expiresInOrStatusFontweight}
                                      expiresInOrStatusBgColor={expiresInOrStatusBgColor}
                                      showSaveToProjects={showSaveToProjects}
                                      tileTextFontSize={tileTextFontSize}
                                      sectionClass={sectionClass}
                                      onSuccess={(msg: string) => {
                                        setSuccessMessage(msg);
                                        setErrorMessage(null);
                                      }}
                                      onError={(msg: string) => {
                                        setErrorMessage(msg);
                                        setSuccessMessage(null);
                                      }}
                                      data={data}
                                      beautyshot={beautyShot}
                                      tileImgContainerBgColor={tileImgContainerBgColor}
                                      activityDateText={activityDateText}
                                    />
                                  );
                                })
                                .filter(Boolean)
                            : false}
                      </div>
                    ) : (
                      <Slider
                        a11y={{
                          enabled: true,
                          prevSlideMessage: ariaLabelOfPreviousButton,
                          nextSlideMessage: ariaLabelOfNextButton,
                          containerRoleDescriptionMessage: '',
                          slideRole: 'presentation',
                          itemRoleDescriptionMessage: '',
                        }}
                        pagination={{
                          enabled: isEnabled,
                          clickable: isProductRecommend, // makes dots clickable
                          dynamicBullets: isProductRecommend, // larger active + neighbor bullets
                        }}
                        onInit={(sw) => setFFIDsForPagination(sw, ffidCurated as string)}
                        slidesPerView={1.4}
                        centeredSlides={!isDesktopSize}
                        centeredSlidesBounds={!isDesktopSize}
                        aria-label={`${mainHeading?.trim() || ''}`}
                        uniqueFFIDText={ffidCurated}
                        dots={false}
                        spaceBetween={12}
                        arrows={shouldShowArrows(sectionClass, { isMobileSize, isTabletSize })}
                        arrowsClassName={clsx('z-20', isProductRecommend ? '-bottom-20' : 'bottom-0')}
                        ariaLabelOfPreviousButton={ariaLabelOfPreviousButton}
                        ariaLabelOfNextButton={ariaLabelOfNextButton}
                        allowTouchMove={true}
                        autoplay={
                          autoRotation && autoRotationSpeed
                            ? {
                                delay: autoRotationSpeed * 1000,
                                disableOnInteraction: true,
                                pauseOnMouseEnter: true,
                              }
                            : false
                        }
                        slidesOffsetAfter={0}
                        loop={false}
                        freeMode={false}
                        centerInsufficientSlides
                        breakpoints={{
                          [mobile]: {
                            spaceBetween: 16,
                            slidesPerView: 2.2,
                            slidesOffsetAfter: 0,
                            centeredSlides: false,
                            centeredSlidesBounds: false,
                          },
                          [desktop]: {
                            slidesOffsetAfter: 0,
                            slidesPerView: isPastOrder ? 3 : slidesForOtherSections,
                          },
                          [mediumDesktop]: {
                            spaceBetween: 20,
                            slidesPerView: isPastOrder ? 3 : slidesForOtherSections,
                          },
                        }}
                        navPosition={mainHeadingPosition === 'right' ? 'left' : 'right'}
                      >
                        {isProductRecommend && mediaclipProducts.length > 0
                          ? limitedMediaclip.map((item: Product, index: number) => (
                              <PersonalizedTile
                                key={`${item?.name}-${index}`}
                                keyprop={index}
                                product={item}
                                beautyshot={beautyshots[item.productKey ?? item.productId ?? '']}
                                previewLabel={tileCtaText}
                                saveToProjects={showSaveToProjects ? 'Save to projects +' : undefined}
                                onSuccess={(msg: string) => {
                                  setSuccessMessage(msg);
                                  setErrorMessage(null);
                                }}
                                onError={(msg: string) => {
                                  setErrorMessage(msg);
                                  setSuccessMessage(null);
                                }}
                                data={data}
                              />
                            ))
                          : isReviewProjects && hasWishlist
                            ? wishlistTiles?.map((tile, index) => (
                                <Tile
                                  ffidCurated={ffidCurated}
                                  expiresFontColor={expiresFontColor}
                                  isSingleCard={isSingleCard}
                                  key={tile?.item?.lineItemId ?? 'tile-' + index}
                                  keyProp={index}
                                  tile={tile}
                                  tileCtaText={tileCtaText}
                                  backImage={backImage}
                                  backgroundEntity={backgroundEntity}
                                  expiresInOrStatus={expiresInOrStatus}
                                  categoryFontweightClass={categoryFontweightClass}
                                  showStatus={showStatus}
                                  showExpiresIn={showExpiresIn}
                                  expiresInOrStatusFontweight={expiresInOrStatusFontweight}
                                  expiresInOrStatusBgColor={expiresInOrStatusBgColor}
                                  showSaveToProjects={showSaveToProjects}
                                  tileTextFontSize={tileTextFontSize}
                                  sectionClass={sectionClass}
                                  activityDateText={activityDateText}
                                />
                              ))
                            : filteredTiles && isProductRecommend
                              ? filteredTiles
                                  ?.slice(0, 4)
                                  ?.map((tile, index) => {
                                    const productKey = tile.productKey as string;
                                    const matchedProduct = productKey ? productMap[productKey] : undefined;
                                    const beautyShot = filteredTileBeautyshots[productKey];

                                    return (
                                      <Tile
                                        ffidCurated={ffidCurated}
                                        expiresFontColor={expiresFontColor}
                                        isSingleCard={isSingleCard}
                                        key={'filteredTiles' + index}
                                        keyProp={index}
                                        tile={tile}
                                        product={matchedProduct}
                                        tileCtaText={tileCtaText}
                                        backImage={backImage}
                                        backgroundEntity={backgroundEntity}
                                        expiresInOrStatus={expiresInOrStatus as string}
                                        categoryFontweightClass={categoryFontweightClass}
                                        showStatus={showStatus}
                                        showExpiresIn={showExpiresIn}
                                        expiresInOrStatusFontweight={expiresInOrStatusFontweight}
                                        expiresInOrStatusBgColor={expiresInOrStatusBgColor}
                                        showSaveToProjects={showSaveToProjects}
                                        tileTextFontSize={tileTextFontSize}
                                        sectionClass={sectionClass}
                                        onSuccess={(msg: string) => {
                                          setSuccessMessage(msg);
                                          setErrorMessage(null);
                                        }}
                                        onError={(msg: string) => {
                                          setErrorMessage(msg);
                                          setSuccessMessage(null);
                                        }}
                                        data={data}
                                        beautyshot={beautyShot}
                                        tileImgContainerBgColor={tileImgContainerBgColor}
                                        activityDateText={activityDateText}
                                      />
                                    );
                                  })
                                  .filter(Boolean)
                              : false}
                      </Slider>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(isProductRecommend || isPastOrder || showReviewProjects) && (
              <CuratedCtaCard
                ffidCurated={ffidCurated}
                classnames={clsx(
                  // On desktop product-recommend, button is aligned with the carousel arrows. Otherwise it is centered full width
                  isProductRecommend && isDesktopSize ? 'absolute bottom-0 left-0 justify-start' : 'w-full',
                  !isSingleCard && `${isPastOrder ? 'mt-45 lg:mt-70' : ''} `,
                )}
                sectionClass={sectionClassnames}
                ctaText={ctaText}
                ctaCardTextFontweightClass={ctaCardTextFontweightClass}
                sectionName={sectionClass}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CuratedSlider;
